import { useState } from 'react'
import { api, ErroreApi } from '../../servizi/api.ts'
import { Avviso } from '../../componenti/Pezzi.tsx'
import { Bottone, Modale, Scelta } from '../../componenti/base.tsx'
import { ORE_SVOLTE, giornoVicino, oreDaDurata, type EsitoSvolta } from '../../servizi/mediazioni.ts'

/**
 * L'esito di una mediazione, dal giorno in cui si fa: svolta, con le ore, oppure «la persona non
 * si è presentata». Li usano il mediatore sulle sue mediazioni e l'operatore dalla scheda.
 */

type Incontro = { id: string; data: string; ora: string; durata_minuti: number }

/**
 * «Segna come svolta»: le ore si scelgono a mezz'ore, partendo dalla durata prevista. Dalle ore
 * nasce l'intervento in bozza sul percorso scelto alla programmazione (457).
 */
export function SegnaSvolta({ mediazione, chiudi, fatto }: {
  mediazione: Incontro
  chiudi: () => void
  fatto: (esito: EsitoSvolta) => void
}) {
  const [ore, setOre] = useState(oreDaDurata(mediazione.durata_minuti))
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function salva() {
    setErrore(null); setInCorso(true)
    try {
      fatto(await api.post<EsitoSvolta>(`/mediazioni/${mediazione.id}/svolta`, { ore: Number(ore) }))
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine. Riprova fra poco.')
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale titolo="Segna la mediazione come svolta" chiudi={chiudi}
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Non ancora</Bottone>
              <Bottone variante="primario" inCorso={inCorso} onClick={() => void salva()}>Segna come svolta</Bottone>
            </>}>
      <p>{giornoVicino(mediazione.data)} alle {mediazione.ora}.</p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <Scelta etichetta="Quanto è durata" obbligatorio valore={ore} opzioni={ORE_SVOLTE} cambia={setOre}
              aiuto="Conta il tempo effettivo della mediazione, a mezz’ore. Le ore vanno nella scheda della persona: chi ha programmato la mediazione le conferma." />
    </Modale>
  )
}

/**
 * «La persona non si è presentata»: chiude la mediazione senza ore. Si chiede conferma, perché
 * non si torna indietro; chi ha programmato la mediazione riceve l'avviso per riprogrammarla.
 */
export function SegnaNonPresentata({ mediazione, nome, chiudi, fatto }: {
  mediazione: Incontro
  /** Nome della persona, se chi guarda lo vede; altrimenti si dice «la persona». */
  nome?: string
  chiudi: () => void
  fatto: () => void
}) {
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function conferma() {
    setErrore(null); setInCorso(true)
    try {
      await api.post(`/mediazioni/${mediazione.id}/non-presentata`, {})
      fatto()
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine. Riprova fra poco.')
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale titolo="La persona non si è presentata?" chiudi={chiudi} fuocoIniziale="azioni"
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Torna indietro</Bottone>
              <Bottone variante="critico" inCorso={inCorso} onClick={() => void conferma()}>
                Sì, non si è presentata
              </Bottone>
            </>}>
      <p>
        {giornoVicino(mediazione.data)} alle {mediazione.ora}{nome ? `, con ${nome}` : ''}: confermi che la persona
        non si è presentata?
      </p>
      <p className="nota-modulo">
        La mediazione si chiude senza ore e non conta come intervento. Chi l’ha programmata riceve un avviso
        per fissarne un’altra, se serve. Una volta confermato non si torna indietro.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}
